<template>
  <LcSwipeable
    :left-action="{
      activationThreshold: 0.4,
      fullSwipe: false,
      icon: 'trash-full',
      label: t('richinbox.message.swipe.action.delete'),
      resistanceThreshold: 0.75,
    }"
    @trigger="handleDelete"
    @swipe-start="isSwiping = true"
    @swipe-end="onSwipeEnd"
  >
    <LcCard
      class="notification"
      :class="{ 'unread': !read, 'has-media': showMedia }"
      shadow="large"
      radius="medium"
      body-class="notification-body"
      @click="handleClick"
    >
      <div v-if="showMedia" class="media-frame mb-150">
        <img class="media" :src="media" alt="" loading="lazy" @error="handleMediaError" />
      </div>
      <span v-if="showMedia && !read" class="unread-bubble" aria-hidden="true" />

      <div class="header">
        <h3
          ref="headlineRef"
          :aria-label="headlineAriaLabel"
          class="headline"
          :class="{
            bold: !read,
            clamped: !isExpanded,
          }"
        >
          {{ headline }}
        </h3>
      </div>

      <div class="body">
        <div ref="measureCollapsedRef" class="measure-collapsed" aria-hidden="true">
          <div class="collapsed mt-050">
            <div class="text" :style="{ '--body-lines': bodyLineClamp }">
              <RichInboxMessageContent :content="message" />
              <span class="read-more">{{ $t('richinbox.message.readmore') }}</span>
            </div>
            <time class="timestamp" :datetime="timestamp">{{ formattedTimestamp }}</time>
          </div>
        </div>

        <Collapse :when="isExpanded" :base-height="collapsedHeight">
          <div v-if="!isExpanded" class="collapsed mt-050">
            <div
              ref="contentRef"
              class="text"
              data-testid="message-text"
              :style="{ '--body-lines': bodyLineClamp }"
            >
              <RichInboxMessageContent
                :content="message"
                :max-chars="isClamped ? truncatedMessage.length : undefined"
              />
              <button
                :aria-expanded="isExpanded"
                class="read-more"
                :class="{ unread: !read }"
                type="button"
                @click.stop="handleClick"
              >
                {{ $t('richinbox.message.readmore') }}
              </button>
            </div>
            <div ref="measureRef" class="text measure" aria-hidden="true">
              <span ref="measureTextRef"></span>
              <span class="read-more">{{ $t('richinbox.message.readmore') }}</span>
            </div>
            <time class="timestamp" :datetime="timestamp">{{ formattedTimestamp }}</time>
          </div>
          <div v-else class="mt-050">
            <RichInboxMessageContent
              :content="message"
              block
              class="message flex flex-col gap-050"
            />
            <LcButton
              v-if="link"
              ref="continueButtonRef"
              class="continue"
              data-testid="continue-button"
              icon="arrow-right"
              icon-position="right"
              @click.stop="handleContinue"
            >
              {{ $t('richinbox.message.continue') }}
            </LcButton>
            <RichInboxMessageContent
              v-if="terms"
              :content="terms"
              block
              class="mt-150 paragraph-small-text"
              data-testid="message-terms"
            />
            <div class="footer">
              <time class="timestamp" :datetime="timestamp">{{ formattedTimestamp }}</time>
              <button
                :aria-label="$t('richinbox.message.delete.arialabel')"
                class="delete"
                data-testid="delete-button"
                type="button"
                @click.stop="handleDelete"
              >
                <LcIcon class="delete-icon" name="trash-full" size="small" />
              </button>
            </div>
          </div>
        </Collapse>
      </div>
    </LcCard>
  </LcSwipeable>
</template>

<script lang="ts" setup>
export type RichInboxMessageProps = {
  headline: string
  id: string
  link: string
  media?: string
  message: string
  read: boolean
  terms?: string
  timestamp: string
}

type RichInboxMessageEmits = {
  delete: [id: string]
  read: [id: string]
}

const { headline, id, link, media, message, read, terms, timestamp } =
  defineProps<RichInboxMessageProps>()
const emit = defineEmits<RichInboxMessageEmits>()

const hasMediaError = ref(false)
const showMedia = computed(() => !!media && !hasMediaError.value)
const handleMediaError = () => {
  hasMediaError.value = true
}

const expandedId = inject(richInboxExpandedIdKey, ref<Maybe<string>>(null))

const { syncReadAndDeletedMessages } = useOptimove()

const plainMessage = computed(() => treeToPlainText(parseMarkdown(message)))

const isExpanded = ref(false)
const isSwiping = ref(false)
const isClamped = ref(false)
const truncatedMessage = ref(plainMessage.value)
const bodyLineClamp = ref(2)
const contentRef = useTemplateRef<HTMLElement>('contentRef')
const measureRef = useTemplateRef<HTMLElement>('measureRef')
const measureTextRef = useTemplateRef<HTMLElement>('measureTextRef')
const measureCollapsedRef = useTemplateRef<HTMLElement>('measureCollapsedRef')
const headlineRef = useTemplateRef<HTMLElement>('headlineRef')
const collapsedHeight = ref(0)
const continueButtonRef = useTemplateRef('continueButtonRef')

watch(expandedId, (newId) => {
  if (isExpanded.value && newId !== id) isExpanded.value = false
})

const { t } = useI18n()

const formattedTimestamp = computed(() => formatTimestamp(timestamp))

const headlineAriaLabel = computed(() =>
  read
    ? undefined
    : t('richinbox.message.arialabel.unread', {
        headline,
      }),
)

const computeTruncation = () => {
  const visibleEl = contentRef.value
  const measureEl = measureRef.value
  const measureTextEl = measureTextRef.value
  if (!visibleEl || !measureEl || !measureTextEl) return

  const plain = plainMessage.value

  isClamped.value = false
  truncatedMessage.value = plain

  const twoLineHeight = visibleEl.clientHeight + 1
  const fitsInTwoLines = (text: string) => {
    measureTextEl.textContent = text
    return measureEl.scrollHeight <= twoLineHeight
  }

  if (fitsInTwoLines(plain)) return

  let lowerBound = 0
  let upperBound = plain.length
  let longestFittingLength = 0
  while (lowerBound <= upperBound) {
    const candidateLength = Math.floor((lowerBound + upperBound) / 2)
    if (fitsInTwoLines(plain.slice(0, candidateLength))) {
      longestFittingLength = candidateLength
      lowerBound = candidateLength + 1
    } else {
      upperBound = candidateLength - 1
    }
  }

  const prefix = plain.slice(0, longestFittingLength).trimEnd()
  const lastSpace = prefix.lastIndexOf(' ')
  truncatedMessage.value = lastSpace > 0 ? prefix.slice(0, lastSpace) : prefix
  isClamped.value = true
}

const onSwipeEnd = () => {
  requestAnimationFrame(() => {
    isSwiping.value = false
  })
}

const handleClick = (event?: MouseEvent) => {
  if (isSwiping.value) return
  const fromKeyboard = event?.detail === 0
  isExpanded.value = !isExpanded.value
  expandedId.value = isExpanded.value ? id : null
  if (isExpanded.value && !read) emit('read', id)
  if (isExpanded.value && fromKeyboard) {
    nextTick(() => {
      requestAnimationFrame(() => {
        const el = continueButtonRef.value?.$el
        if (!(el instanceof HTMLElement)) return
        const focusTarget = el.matches('button') ? el : el.querySelector('button')
        focusTarget?.focus()
      })
    })
  }
}

const handleContinue = async () => {
  await syncReadAndDeletedMessages()
  await navigateTo(link, { external: true })
}

const handleDelete = () => {
  emit('delete', id)
}

const recalculateLayout = () => {
  const headlineEl = headlineRef.value
  if (headlineEl) {
    const lineHeight = parseFloat(getComputedStyle(headlineEl).lineHeight)
    if (lineHeight > 0) {
      const renderedLines = Math.max(
        1,
        Math.min(2, Math.round(headlineEl.clientHeight / lineHeight)),
      )
      bodyLineClamp.value = 3 - renderedLines
    }
  }
  nextTick(computeTruncation)
}

watchEffect(
  (onCleanup) => {
    const contentEl = contentRef.value
    const headlineEl = headlineRef.value
    if (!contentEl || !headlineEl) return

    recalculateLayout()

    let lastContentWidth = contentEl.clientWidth
    let lastHeadlineWidth = headlineEl.clientWidth
    const observer = new ResizeObserver(() => {
      const contentWidth = contentEl.clientWidth
      const headlineWidth = headlineEl.clientWidth
      if (contentWidth === lastContentWidth && headlineWidth === lastHeadlineWidth) return
      lastContentWidth = contentWidth
      lastHeadlineWidth = headlineWidth
      isClamped.value = false
      truncatedMessage.value = plainMessage.value
      nextTick(recalculateLayout)
    })
    observer.observe(contentEl)
    observer.observe(headlineEl)
    onCleanup(() => observer.disconnect())
  },
  { flush: 'post' },
)

watchEffect(
  (onCleanup) => {
    const el = measureCollapsedRef.value
    if (!el) return

    collapsedHeight.value = el.offsetHeight

    const observer = new ResizeObserver(() => {
      collapsedHeight.value = el.offsetHeight
    })
    observer.observe(el)
    onCleanup(() => observer.disconnect())
  },
  { flush: 'post' },
)
</script>

<style lang="scss" scoped>
.notification {
  position: relative;
  padding: token('size/space/gap/150');
  background: var(--color-background-primary-primary, white);
  cursor: pointer;

  /* stylelint-disable-next-line selector-class-pattern */
  :deep(.lc-card__body-wrap) {
    overflow: visible;
  }

  :deep(.notification-body) {
    padding: token('size/space/gap/none');
  }

  .header {
    display: flex;
    gap: token('size/space/gap/100');
    align-items: flex-start;
  }

  &.unread:not(.has-media) .header::after {
    content: '';
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--color-red-600);
  }

  .media-frame {
    overflow: hidden;
    width: 100%;
    min-width: 17.5rem;
    max-width: 27.5rem;
    height: 10rem;
    border-radius: token('size/radius/m');
  }

  .media {
    object-fit: none;
    object-position: center;
    width: 100%;
    height: 100%;
  }

  .unread-bubble {
    position: absolute;
    top: 0.4375rem;
    right: 0.625rem;
    box-sizing: content-box;
    width: 0.5rem;
    height: 0.5rem;
    border: token('size/border/200') solid token('color/neutral/white');
    border-radius: 50%;
    background-color: var(--color-red-600);
  }

  .headline {
    @include token-paragraph-regular;

    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    font-weight: normal;

    &.bold {
      font-weight: token('font/paragraph/bold/weight');
    }

    &.clamped {
      display: -webkit-box;
      overflow: hidden;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      line-clamp: 2;
    }
  }

  .body {
    position: relative;
  }

  .measure-collapsed {
    position: absolute;
    inset: 0 0 auto;
    overflow: hidden;
    visibility: hidden;
    pointer-events: none;
  }

  .collapsed {
    position: relative;
  }

  .text {
    overflow: hidden;
    max-height: calc(var(--body-lines, 2) * 1lh);

    &.measure {
      position: absolute;
      inset: 0 0 auto;
      overflow: visible;
      max-height: none;
      visibility: hidden;
      pointer-events: none;
    }
  }

  .message {
    @include token-paragraph-regular;

    :deep(.message-paragraph) {
      margin: 0;
    }
  }

  :deep(sup) {
    position: relative;
    top: -0.4em;
    vertical-align: baseline;
    font-size: 0.75em;
    line-height: 0;
  }

  .read-more {
    padding: 0;
    border: none;
    background: transparent;
    color: token('color/blue/600');
    font: inherit;
    cursor: pointer;

    &::before {
      content: '…';
      display: inline-block;
      margin-right: token('size/space/gap/050');
      color: token('color/text/primary/primary');
      text-decoration: none;
      cursor: pointer;
    }

    &.unread {
      text-decoration: underline;
    }

    &:focus-visible {
      border-radius: token('size/border/200');
      outline: token('size/border/200') solid currentcolor;
      outline-offset: token('size/border/200');
    }
  }

  .continue {
    display: block;
    width: 100%;
    margin-top: token('size/space/gap/150');
  }

  .footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: token('size/space/gap/100');
  }

  .delete {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    border: none;
    background: transparent;
    color: inherit;
    cursor: pointer;

    &:focus-visible {
      border-radius: token('size/border/200');
      outline: token('size/border/200') solid currentcolor;
      outline-offset: token('size/border/200');
    }
  }

  .delete-icon {
    width: 1rem;
    height: 1rem;
  }

  .timestamp {
    @include token-caption-regular;

    display: block;
    margin-top: token('size/space/gap/150');
    color: token('color/text/secondary/secondary');

    &::first-letter {
      text-transform: uppercase;
    }
  }

  .footer .timestamp {
    margin-top: 0;
  }
}
</style>
